import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
 RefreshCw,
 Wallet,
 ArrowUpRight,
 Building2,
 Percent,
 Banknote,
 CreditCard,
 ExternalLink,
 ListChecks,
 Loader2,
 Filter,
 Receipt,
 RefreshCcw,
 Crown,
 TrendingUp,
 ArrowLeftRight,
 X,
} from 'lucide-react';
import {
 getAdminAccountingOverview,
 getAdminVendorAccountingStatement,
} from '../../services/vendorPayoutService';
import { useAdminVendors } from '../../hooks/useAdminVendors';
import { useAdminRevenue } from '../../hooks/useAdminRevenue';
import { formatDateTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import { SearchSelect } from '../../components/shared';
import DatePicker from '../../components/client/DatePicker';

const fmtMoney = (n) => {
 const v = Number(n);
 if (Number.isNaN(v)) return '$0.00';
 return `$${v.toFixed(2)}`;
};

const MetricCard = ({ label, value, hint, icon: Icon, tone = 'sky' }) => {
 const tones = {
 sky: 'bg-surface-muted text-ink',
 amber: 'bg-amber-50 text-amber-700',
 emerald: 'bg-emerald-50 text-emerald-700',
 rose: 'bg-rose-50 text-rose-700',
 gray: 'bg-gray-100 text-ink-secondary',
 };
 return (
 <div className="bg-surface rounded-lg border border-border p-4 shadow-sm">
 <div className="flex items-start justify-between gap-2">
 <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-tertiary leading-tight">
 {label}
 </span>
 {Icon && (
 <div className={`p-1.5 rounded-lg shrink-0 ${tones[tone]}`}>
 <Icon className="w-4 h-4" />
 </div>
 )}
 </div>
 <p className="text-xl font-bold text-ink mt-2 tabular-nums">{value}</p>
 {hint ? <p className="text-[11px] text-ink-tertiary mt-1">{hint}</p> : null}
 </div>
 );
};

const SectionCard = ({ title, subtitle, action, children }) => (
 <div className="bg-surface rounded-lg border border-border shadow-sm overflow-hidden">
 <div className="px-5 py-4 border-b border-border flex flex-wrap items-start justify-between gap-3">
 <div>
 <h3 className="text-sm font-semibold text-ink">{title}</h3>
 {subtitle ? <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p> : null}
 </div>
 {action}
 </div>
 <div className="p-5">{children}</div>
 </div>
);

const AdminAccounting = () => {
 const [searchParams, setSearchParams] = useSearchParams();
 const initialVendorId = searchParams.get('vendor_id') || '';
 const [selectedVendorId, setSelectedVendorId] = useState(initialVendorId);
 const [dateFrom, setDateFrom] = useState('');
 const [dateTo, setDateTo] = useState('');

 const overviewQuery = useQuery({
 queryKey: ['admin-accounting-overview'],
 queryFn: async () => getAdminAccountingOverview(),
 });

 const { data: vendorsResponse, isLoading: vendorsLoading } = useAdminVendors({
 per_page: 500,
 });

 const {
 revenue,
 isLoading: revenueLoading,
 period,
 setPeriod,
 periodOptions,
 refetch: refetchRevenue,
 } = useAdminRevenue('month');

 const vendorOptions = useMemo(() => {
 const vendors = vendorsResponse?.data?.data || vendorsResponse?.data || [];
 if (!Array.isArray(vendors)) return [];
 return vendors
 .filter((v) => v?.vendor_profile?.id)
 .map((v) => {
 const vp = v.vendor_profile;
 const email = v.email || vp?.user?.email || '';
 return {
 value: String(vp.id),
 label: email
 ? `${vp.business_name || v.name || 'Vendor'} · ${email}`
 : vp.business_name || v.name || 'Vendor',
 business_name: vp.business_name || v.name || 'Vendor',
 user_id: v.id || vp.user_id,
 email,
 };
 })
 .sort((a, b) => a.label.localeCompare(b.label));
 }, [vendorsResponse]);

 const selectedVendorMeta = useMemo(
 () => vendorOptions.find((o) => String(o.value) === String(selectedVendorId)) || null,
 [vendorOptions, selectedVendorId]
 );

 useEffect(() => {
 const fromUrl = searchParams.get('vendor_id') || '';
 if (fromUrl && fromUrl !== selectedVendorId) {
 setSelectedVendorId(fromUrl);
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [searchParams]);

 const statementParams = useMemo(
 () => ({
 ...(dateFrom ? { date_from: dateFrom } : {}),
 ...(dateTo ? { date_to: dateTo } : {}),
 limit: 250,
 }),
 [dateFrom, dateTo]
 );

 const statementQuery = useQuery({
 queryKey: ['admin-accounting-statement', selectedVendorId, statementParams],
 queryFn: async () => getAdminVendorAccountingStatement(selectedVendorId, statementParams),
 enabled: Boolean(selectedVendorId),
 });

 const overview = overviewQuery.data?.data;
 const marketplace = overview?.marketplace || {};
 const subscriptions = overview?.subscriptions || {};
 const platform = overview?.platform || {};
 const statement = statementQuery.data?.data;
 const entries = statement?.entries ?? [];
 const summary = statement?.summary;
 const statementVendor = statement?.vendor;

 const handleSelectVendor = (value) => {
 const id = value && value !== 'all' ? String(value) : '';
 setSelectedVendorId(id);
 if (id) {
 setSearchParams({ vendor_id: id });
 } else {
 setSearchParams({});
 }
 };

 const clearVendor = () => {
 setSelectedVendorId('');
 setSearchParams({});
 setDateFrom('');
 setDateTo('');
 };

 const refreshing =
 overviewQuery.isFetching || revenueLoading || statementQuery.isFetching;

 const revenueOverview = revenue?.overview || {};

 return (
 <div className="space-y-5 max-w-[1400px] mx-auto pb-12">
 <AdminPageHeader
 title="Accounting"
 subtitle="Full platform books — marketplace payments, fees, vendor ledgers, subscriptions, refunds, and period revenue in one place."
 actions={
 <div className="flex flex-wrap items-center gap-2">
 <Link
 to="/admin/vendor-payouts"
 className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink-secondary hover:bg-surface-muted shadow-sm whitespace-nowrap"
 >
 <Banknote className="w-4 h-4 text-amber-600" />
 Settle payouts
 </Link>
 <button
 type="button"
 onClick={() => {
 overviewQuery.refetch();
 refetchRevenue();
 if (selectedVendorId) statementQuery.refetch();
 }}
 disabled={refreshing}
 className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-surface text-sm font-medium text-ink-secondary hover:bg-surface-muted shadow-sm disabled:opacity-50 whitespace-nowrap"
 >
 <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
 Refresh
 </button>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.accounting.title}
 steps={ADMIN_PAGE_GUIDES.accounting.steps}
 icon={ListChecks}
 />

 {/* Platform P&L snapshot */}
 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
 <MetricCard
 label="Platform income"
 value={overviewQuery.isLoading ? '…' : fmtMoney(platform.total_income ?? overview?.platform_income_total)}
 hint="Fees + subscription revenue"
 icon={TrendingUp}
 tone="emerald"
 />
 <MetricCard
 label="Marketplace fees"
 value={overviewQuery.isLoading ? '…' : fmtMoney(platform.fee_income ?? overview?.platform_fees_collected)}
 hint="Commission from bookings"
 icon={Percent}
 tone="sky"
 />
 <MetricCard
 label="Subscription revenue"
 value={overviewQuery.isLoading ? '…' : fmtMoney(subscriptions.revenue ?? overview?.subscription_revenue)}
 hint={`${subscriptions.payments_count ?? overview?.subscription_payments_count ?? 0} completed plan payments`}
 icon={Crown}
 tone="sky"
 />
 <MetricCard
 label="Vendor liability"
 value={overviewQuery.isLoading ? '…' : fmtMoney(platform.vendor_liability ?? overview?.vendor_balance_due)}
 hint={`${marketplace.vendors_due ?? overview?.vendors_with_balance_due ?? 0} vendors with balance due`}
 icon={Wallet}
 tone="amber"
 />
 </div>

 {/* Money modules */}
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
 <SectionCard
 title="Marketplace"
 subtitle="Client payments through the platform"
 action={
 <Link
 to="/admin/payments"
 className="text-xs font-medium text-ink hover:underline inline-flex items-center gap-1"
 >
 Payments <ExternalLink className="w-3 h-3" />
 </Link>
 }
 >
 <div className="space-y-3 text-sm">
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Gross client payments</span>
 <span className="font-semibold tabular-nums">{fmtMoney(marketplace.gross ?? overview?.client_payments_gross)}</span>
 </div>
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Platform fees kept</span>
 <span className="font-semibold tabular-nums text-emerald-700">
 {fmtMoney(marketplace.platform_fees ?? overview?.platform_fees_collected)}
 </span>
 </div>
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Vendor earnings (owed)</span>
 <span className="font-semibold tabular-nums">{fmtMoney(marketplace.vendor_share ?? overview?.vendor_earnings_owed)}</span>
 </div>
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Disbursements sent</span>
 <span className="font-semibold tabular-nums">{fmtMoney(marketplace.disbursed ?? overview?.vendor_disbursements_sent)}</span>
 </div>
 <div className="flex justify-between gap-3 pt-2 border-t border-border">
 <span className="text-gray-500">Balance still due</span>
 <span className="font-bold tabular-nums text-amber-700">
 {fmtMoney(marketplace.balance_due ?? overview?.vendor_balance_due)}
 </span>
 </div>
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Settlement rate</span>
 <span className="font-semibold tabular-nums">
 {marketplace.settlement_rate ?? overview?.settlement_rate ?? 0}%
 </span>
 </div>
 <div className="pt-2 flex flex-wrap gap-2">
 <Link
 to="/admin/vendor-payouts"
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-50 text-amber-800"
 >
 <Banknote className="w-3.5 h-3.5" /> Settle payouts
 </Link>
 </div>
 </div>
 </SectionCard>

 <SectionCard
 title="Subscriptions"
 subtitle="Vendor plan payments to vendaList"
 action={
 <Link
 to="/admin/subscription-payments"
 className="text-xs font-medium text-ink hover:underline inline-flex items-center gap-1"
 >
 Sub payments <ExternalLink className="w-3 h-3" />
 </Link>
 }
 >
 <div className="space-y-3 text-sm">
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Completed revenue</span>
 <span className="font-semibold tabular-nums text-emerald-700">
 {fmtMoney(subscriptions.revenue ?? overview?.subscription_revenue)}
 </span>
 </div>
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Payments recorded</span>
 <span className="font-semibold tabular-nums">
 {subscriptions.payments_count ?? overview?.subscription_payments_count ?? 0}
 </span>
 </div>
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Refunded</span>
 <span className="font-semibold tabular-nums text-rose-700">
 {fmtMoney(subscriptions.refunded ?? overview?.subscription_refunded)}
 </span>
 </div>
 <div className="flex justify-between gap-3 pt-2 border-t border-border">
 <span className="text-gray-500">Net subscription income</span>
 <span className="font-bold tabular-nums">
 {fmtMoney(subscriptions.net ?? Math.max(0, (subscriptions.revenue || 0) - (subscriptions.refunded || 0)))}
 </span>
 </div>
 <div className="pt-2 flex flex-wrap gap-2">
 <Link
 to="/admin/subscriptions"
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-surface-muted text-ink"
 >
 <Crown className="w-3.5 h-3.5" /> Plans
 </Link>
 <Link
 to="/admin/subscription-payments"
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 text-gray-700"
 >
 <Receipt className="w-3.5 h-3.5" /> Payment history
 </Link>
 </div>
 </div>
 </SectionCard>

 <SectionCard
 title="Refunds & cash outs"
 subtitle="Money returned or paid to vendors"
 action={
 <Link
 to="/admin/refunds"
 className="text-xs font-medium text-ink hover:underline inline-flex items-center gap-1"
 >
 Refunds <ExternalLink className="w-3 h-3" />
 </Link>
 }
 >
 <div className="space-y-3 text-sm">
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Booking refunds</span>
 <span className="font-semibold tabular-nums text-rose-700">
 {fmtMoney(marketplace.refunds_total ?? overview?.booking_refunds_total)}
 </span>
 </div>
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Refund count</span>
 <span className="font-semibold tabular-nums">
 {marketplace.refunds_count ?? overview?.booking_refunds_count ?? 0}
 </span>
 </div>
 <div className="flex justify-between gap-3">
 <span className="text-gray-500">Vendor payouts sent</span>
 <span className="font-semibold tabular-nums">
 {fmtMoney(marketplace.disbursed ?? overview?.vendor_disbursements_sent)}
 </span>
 </div>
 <div className="flex justify-between gap-3 pt-2 border-t border-border">
 <span className="text-gray-500">Still payable to vendors</span>
 <span className="font-bold tabular-nums text-amber-700">
 {fmtMoney(marketplace.balance_due ?? overview?.vendor_balance_due)}
 </span>
 </div>
 <div className="pt-2 flex flex-wrap gap-2">
 <Link
 to="/admin/refunds"
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-rose-50 text-rose-800"
 >
 <RefreshCcw className="w-3.5 h-3.5" /> Review refunds
 </Link>
 <Link
 to="/admin/revenue"
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 text-gray-700"
 >
 <TrendingUp className="w-3.5 h-3.5" /> Revenue report
 </Link>
 </div>
 </div>
 </SectionCard>
 </div>

 {/* Period revenue */}
 <SectionCard
 title="Period revenue snapshot"
 subtitle="Booking-side revenue for the selected window (excludes private bookings where applicable)"
 action={
 <div className="flex flex-wrap items-center gap-2">
 <SearchSelect
 options={periodOptions}
 value={period}
 onChange={setPeriod}
 placeholder="Period"
 className="min-w-[160px]"
 />
 <Link
 to="/admin/revenue"
 className="text-xs font-medium text-ink hover:underline inline-flex items-center gap-1 whitespace-nowrap"
 >
 Full revenue <ExternalLink className="w-3 h-3" />
 </Link>
 </div>
 }
 >
 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
 <MetricCard
 label="Total revenue"
 value={revenueLoading ? '…' : fmtMoney(revenueOverview.total_revenue)}
 icon={CreditCard}
 tone="sky"
 />
 <MetricCard
 label="Platform fees"
 value={revenueLoading ? '…' : fmtMoney(revenueOverview.platform_fees)}
 icon={Percent}
 tone="emerald"
 />
 <MetricCard
 label="Vendor share"
 value={revenueLoading ? '…' : fmtMoney(revenueOverview.vendor_payouts)}
 icon={ArrowUpRight}
 tone="sky"
 />
 <MetricCard
 label="Processor fees"
 value={revenueLoading ? '…' : fmtMoney(revenueOverview.processor_fees)}
 icon={Receipt}
 tone="gray"
 />
 <MetricCard
 label="Net revenue"
 value={revenueLoading ? '…' : fmtMoney(revenueOverview.net_revenue)}
 icon={TrendingUp}
 tone="emerald"
 />
 <MetricCard
 label="Transactions"
 value={revenueLoading ? '…' : String(revenueOverview.total_transactions ?? 0)}
 icon={ArrowLeftRight}
 tone="gray"
 />
 </div>
 </SectionCard>

 {/* Vendor ledger via search */}
 <SectionCard
 title="Vendor ledger"
 subtitle="Search a vendor to open their order-earnings statement (credits in, payouts out, running balance)"
 >
 <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-5">
 <div className="flex-1 min-w-0">
 <label className="block text-xs font-medium text-ink-secondary mb-1.5">
 Find vendor
 </label>
 <SearchSelect
 options={[{ value: '', label: 'Search or select a vendor…' }, ...vendorOptions]}
 value={selectedVendorId || ''}
 onChange={handleSelectVendor}
 placeholder={vendorsLoading ? 'Loading vendors…' : 'Search vendor by name or email…'}
 disabled={vendorsLoading}
 />
 </div>
 {selectedVendorId && (
 <button
 type="button"
 onClick={clearVendor}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border text-ink-secondary hover:bg-surface-muted whitespace-nowrap"
 >
 <X className="w-4 h-4" />
 Clear
 </button>
 )}
 </div>

 {!selectedVendorId ? (
 <div className="product-surface-muted border-dashed px-5 py-10 text-center">
 <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-3" />
 <p className="text-sm font-medium text-ink-secondary">
 Search a vendor to open their ledger
 </p>
 <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
 Same search pattern as other admin screens — pick a business, then review earnings,
 payouts, and balance due.
 </p>
 </div>
 ) : statementQuery.isLoading ? (
 <div className="py-16 text-center">
 <Loader2 className="w-7 h-7 animate-spin text-ink mx-auto" />
 </div>
 ) : statementQuery.error ? (
 <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm px-4 py-3">
 {statementQuery.error.message || 'Could not load vendor statement.'}
 </div>
 ) : (
 <div className="space-y-5">
 <div className="flex flex-wrap items-start justify-between gap-3">
 <div>
 <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
 Selected account
 </p>
 <h4 className="text-lg font-semibold text-ink mt-0.5">
 {statementVendor?.business_name || selectedVendorMeta?.business_name || 'Vendor'}
 </h4>
 <p className="text-sm text-gray-500 mt-0.5">
 {selectedVendorMeta?.email || '—'}
 </p>
 </div>
 <div className="flex flex-wrap gap-2">
 {(statementVendor?.user_id || selectedVendorMeta?.user_id) && (
 <Link
 to={`/admin/vendors/${statementVendor?.user_id || selectedVendorMeta?.user_id}`}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border text-ink-secondary hover:bg-surface-muted"
 >
 Profile <ExternalLink className="w-3.5 h-3.5" />
 </Link>
 )}
 <Link
 to={`/admin/vendor-payouts?vendor_id=${selectedVendorId}`}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-ink text-white hover:bg-black"
 >
 Record payout
 </Link>
 </div>
 </div>

 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
 <div className="rounded-xl bg-surface-muted/80 p-3">
 <p className="text-[10px] uppercase font-semibold text-ink">
 Earned
 </p>
 <p className="text-lg font-bold tabular-nums text-ink">
 {fmtMoney(summary?.total_earned_from_orders)}
 </p>
 </div>
 <div className="rounded-xl bg-surface-muted/60 p-3">
 <p className="text-[10px] uppercase font-semibold text-gray-500">Paid out</p>
 <p className="text-lg font-bold tabular-nums text-ink">
 {fmtMoney(summary?.total_disbursed)}
 </p>
 </div>
 <div className="rounded-xl bg-amber-50/80 p-3">
 <p className="text-[10px] uppercase font-semibold text-amber-700">
 Balance due
 </p>
 <p className="text-lg font-bold tabular-nums text-ink">
 {fmtMoney(summary?.available_balance)}
 </p>
 </div>
 <div className="rounded-xl bg-emerald-50/80 p-3">
 <p className="text-[10px] uppercase font-semibold text-emerald-700">
 Ledger remaining
 </p>
 <p className="text-lg font-bold tabular-nums text-ink">
 {fmtMoney(summary?.remaining_after_disbursements)}
 </p>
 </div>
 </div>

 <div className="flex flex-wrap items-center gap-2 justify-between">
 <p className="text-xs text-gray-500">
 Statement · credits (earnings) and debits (payouts)
 </p>
 <div className="flex flex-wrap items-center gap-2">
 <Filter className="w-3.5 h-3.5 text-gray-400" />
 <div className="w-[150px]">
 <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="From" size="sm" />
 </div>
 <div className="w-[150px]">
 <DatePicker value={dateTo} onChange={setDateTo} placeholder="To" size="sm" />
 </div>
 {(dateFrom || dateTo) && (
 <button
 type="button"
 onClick={() => {
 setDateFrom('');
 setDateTo('');
 }}
 className="text-xs text-ink font-medium px-2"
 >
 Clear dates
 </button>
 )}
 </div>
 </div>

 {entries.length === 0 ? (
 <div className="py-10 text-center text-sm text-gray-500 border border-dashed border-border rounded-xl">
 No ledger entries for this period.
 </div>
 ) : (
 <div className="overflow-x-auto -mx-1 border border-border rounded-xl">
 <table className="min-w-full text-sm">
 <thead className="bg-surface-muted text-left text-[11px] uppercase tracking-wider text-gray-500">
 <tr>
 <th className="px-4 py-3 font-semibold">Date</th>
 <th className="px-4 py-3 font-semibold">Entry</th>
 <th className="px-4 py-3 font-semibold">Reference</th>
 <th className="px-4 py-3 font-semibold text-right">Credit</th>
 <th className="px-4 py-3 font-semibold text-right">Debit</th>
 <th className="px-4 py-3 font-semibold text-right">Balance</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100">
 {entries.map((row) => {
 const isCredit = row.type === 'credit';
 return (
 <tr key={row.id} className="hover:bg-surface-muted/80">
 <td className="px-4 py-3 whitespace-nowrap text-ink-secondary text-xs">
 {row.occurred_at ? formatDateTime(row.occurred_at) : '—'}
 </td>
 <td className="px-4 py-3 min-w-[200px]">
 <div className="flex items-start gap-2">
 <span
 className={`mt-0.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${ isCredit ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800' }`}
 >
 {isCredit ? 'In' : 'Out'}
 </span>
 <div>
 <p className="font-medium text-ink">
 {row.label}
 </p>
 <p className="text-xs text-gray-500 mt-0.5">
 {row.description || '—'}
 </p>
 {isCredit && row.platform_fee != null && (
 <p className="text-[11px] text-gray-400 mt-0.5">
 Gross {fmtMoney(row.gross_amount)} · Fee{' '}
 {fmtMoney(row.platform_fee)}
 </p>
 )}
 </div>
 </div>
 </td>
 <td className="px-4 py-3 text-xs text-gray-500 font-mono">
 {row.reference || row.booking_reference || '—'}
 </td>
 <td className="px-4 py-3 text-right tabular-nums font-medium text-emerald-700">
 {isCredit ? fmtMoney(row.amount) : '—'}
 </td>
 <td className="px-4 py-3 text-right tabular-nums font-medium text-amber-700">
 {!isCredit ? fmtMoney(row.amount) : '—'}
 </td>
 <td className="px-4 py-3 text-right tabular-nums font-semibold text-ink">
 {fmtMoney(row.running_balance)}
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}
 </div>
 )}
 </SectionCard>

 <div className="rounded-lg border border-border bg-gradient-to-r from-surface-muted/80 via-white to-amber-50/80 px-4 py-3 text-sm text-ink-secondary">
 <span className="font-medium text-ink">Books legend: </span>
 Marketplace fees and subscription plan payments are platform income. Vendor share sits as
 liability until you record a payout. Booking refunds reduce client payment history. Private /
 manual bookings stay off the platform ledger.
 </div>
 </div>
 );
};

export default AdminAccounting;
